"use client";

import { useEffect } from "react";

// global-error.tsx must replace the entire <html> document and stays
// minimal — i18n providers may have failed at this point. Bilingual
// copy is hardcoded; layout is a stripped-down version of
// global-not-found.tsx so brand identity is preserved on hard crashes.
type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

const COPY = {
  eyebrowTr: "Bir aksilik",
  eyebrowEn: "Something broke",
  titleTr: "Bir şeyler ters gitti.",
  titleEn: "Something went wrong.",
  ledeTr: "Beklenmedik bir hata oluştu. Tekrar denemeyi deneyebilirsin.",
  ledeEn: "An unexpected error occurred. You can try again.",
  tryAgainTr: "Tekrar dene",
  tryAgainEn: "Try again",
  goHomeTr: "Ana sayfaya dön",
  goHomeEn: "Back to home",
};

export default function GlobalError({ error, reset }: Props) {
  useEffect(() => {
    console.error("[global error boundary]", error);
  }, [error]);

  return (
    // Bilingual COPY shows TR + EN side by side, but the document needs
    // a single lang attribute. We default to "tr" (the site's primary
    // locale per i18n/routing.ts) so screen readers fall back to Turkish
    // pronunciation rather than English. The English half is a courtesy
    // gloss, not the canonical text.
    <html lang="tr">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          background: "#0A0A0F",
          color: "#F5F5F7",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        <main
          id="main"
          style={{
            position: "relative",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "120px 20px 80px",
            maxWidth: 1100,
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >
          <p
            style={{
              margin: 0,
              marginBottom: 20,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              color: "#3B7EFF",
            }}
          >
            {COPY.eyebrowTr} · {COPY.eyebrowEn}
          </p>

          <h1
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: "clamp(32px, 5vw, 64px)",
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "#F5F5F7",
            }}
          >
            {COPY.titleTr}
          </h1>
          <h2
            style={{
              margin: 0,
              marginTop: 8,
              fontWeight: 500,
              fontSize: "clamp(20px, 2.5vw, 28px)",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              color: "#C7C7CE",
            }}
          >
            {COPY.titleEn}
          </h2>

          <p
            style={{
              marginTop: 24,
              maxWidth: 620,
              fontSize: 16,
              lineHeight: 1.6,
              color: "#C7C7CE",
            }}
          >
            {COPY.ledeTr} / {COPY.ledeEn}
          </p>

          <div
            style={{
              marginTop: 40,
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{
                cursor: "pointer",
                border: "none",
                padding: "12px 24px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                color: "#FFFFFF",
                background:
                  "linear-gradient(135deg, #1E3FE8 0%, #3B7EFF 100%)",
                boxShadow:
                  "0 0 0 1px rgba(59,126,255,0.45), 0 12px 32px rgba(30,63,232,0.35)",
              }}
            >
              {COPY.tryAgainTr} / {COPY.tryAgainEn}
            </button>
            {/*
              Plain anchor on purpose: global-error.tsx replaces the entire
              <html> document because the root layout failed. The next-intl
              Link won't work (no provider), and the next/link client router
              context may itself be the thing that crashed. A hard navigation
              to "/" is the safer recovery — middleware re-resolves the
              locale from cookie/header and serves a fresh page.
            */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "12px 24px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 15,
                color: "#F5F5F7",
                textDecoration: "none",
                border: "1px solid rgba(245,245,247,0.14)",
                background: "transparent",
              }}
            >
              {COPY.goHomeTr} / {COPY.goHomeEn}
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
